import { METRIC } from "../types";
import MultibedSelectors from "../../reducers/multi-bed-reducer";
import SubProjectService from "./sub-project-service";
import MultiBedService from "./multibed-service";

const ScaleService = {

    /**
     * ScaleService.setScale(this.props)
     * @param scaleValue
     * @param props - React this.props object
     *
     * @example - setup mapStateToProps
     *  {
     *   scaleData: scaleData,
     *   unit: state.localization.unit,
     *  };
     *
     * @example Set a variable scaleData for mapStateToProps
     * let scaleData ={};
     * if (
     *   state.project &&
     *   state.project.project &&
     *   state.project.project.projectType
     * ) {
     *
     *    scaleData["projectType"] = state.project.project.projectType;
     *
     *    scaleData["scaleValue"] = ( state.project.project.projectType === "CHAMBER" )
     *         ? state.globals.scaleValue
     *        : state.pipeGlobals.scaleValue;
     *
     *    scaleData["scalePixels"] = ( state.project.project.projectType === "CHAMBER" )
     *        ? state.globals.scalePixels
     *        : state.pipeGlobals.scalePixels;
     *
     * } else {
     *    scaleData = {};
     * }
     *
     *
     * then add
     * scaleData: scaleData,
     *
     * @example - setup mapDispatchToProps
     *  {
     *   loadingBed,
     *   modalAlertActions,
     *   setDirtyStateForMultiBeds,
     *   scaleUnitValueChange,
     *   emitLineLenght,
     *  }
     * @returns {Promise<boolean>}
     */
    setScale: async ( props ) => {
        let { scaleValue, scalePixels } = props.scaleData;
        try {
            setTimeout(() => {
                // console.log('(1)',{ scaleValue, scalePixels });
                props.scaleUnitValueChange(ScaleService._convertUnitValues(scaleValue, props));
                props.emitLineLenght(Number(scalePixels));
            });
            setTimeout(() => {
                // console.log('(2)',{ scaleValue, scalePixels });
                props.scaleUnitValueChange(ScaleService._convertUnitValues(scaleValue, props));
                props.emitLineLenght(Number(scalePixels));
            }, 1000);
        } catch (error) {
            console.error("Error on ScaleService.setScale() :: ", error.message);
        }
    },

    /**
     *
     * @param props - React this.props object
     *
     * @example - setup mapStateToProps
     *  {
     *   imperialUnit: state.pdf.imperialUnit,
     *   pdfPageSizes: state.pdf.pdfPageSizes,
     *   imgPageSizes: state.pdf.imgPageSizes,
     *   scaleValue: getScaleValueByState(state),
     *   scalePixels: getScalePixelsByState(state),
     *   scalingImageDataPresent: state.pdf.scalingImageDataPresent,
     *   unit: state.localization.unit,
     *  };
     *
     * @example - setup mapDispatchToProps
     *  {
     *   loadingBed,
     *   modalAlertActions,
     *   setDirtyStateForMultiBeds
     *  }
     * @returns {Promise<boolean>}
     */
    updateScaleMultibed: async ( props ) => {
        try {
            props.loadingBed(true);
            const syncBeds = [...MultibedSelectors.getAllBeds()];
            let placeholderObj, projectInstance;

            for (let bed of syncBeds) {
                placeholderObj = undefined;
                projectInstance = undefined;
                await SubProjectService.clearViewPortForMultiBed(props);
                projectInstance = await SubProjectService.loadSubProject(bed.id, props);
                if (projectInstance.projectType === "PIPE") {
                    const {
                        length,
                        width,
                    } = this.props.pipeBed.dimensions;
                    this.props.setPipeDesign(width.ft, length.ft);
                }
                await ScaleService.setScale(props);
                await ScaleService._waitFor(3000);
                // Save current bed
                const config = { bedName: bed.bedName };
                const subProjectInstance = await SubProjectService.saveOrCreateSubProject(bed.id, config, props);
                await SubProjectService.syncAutomationProcess(subProjectInstance, props);
                // Save the current placeholder
                placeholderObj = MultiBedService.getPlaceholderSettings(bed.name, props);
                placeholderObj.bedId = bed.id;
                props.updatePlaceholder(placeholderObj);
            }
            props.loadingBed(false);
            props.setDirtyStateForMultiBeds(false);
            return true;
        } catch (error) {
            console.error("Error on ScaleService.updateScaleMultibed() :: ", error.message);
            return false;
        }
    },


    /**
     * Executes Scale on project
     * @param executionType
     * @param planScaleFeet
     * @param planScaleMts
     * @param planScaleInches
     * @param props
     *
     * @required mapStateToProps
     * imperialUnit: state.pdf.imperialUnit,
     * pdfPageSizes: state.pdf.pdfPageSizes,
     * imgPageSizes: state.pdf.imgPageSizes,
     * scaleValue: getScaleValueByState(state),
     * scalePixels: getScalePixelsByState(state),
     * scalingImageDataPresent: state.pdf.scalingImageDataPresent,
     * unit: state.localization.unit,
     *
     *
     *
     @required mapDispatchToProps
     * clearRulersOnScreen
     * emitLineLenght
     * scaleUnitValueChange
     *
     * @returns {Promise<void>}
     */
    setScaleProcess: async ( executionType, planScaleFeet = 0, planScaleMts = 0, planScaleInches = 0, props ) => {
        try {
            props.clearRulersOnScreen(); //Clears scale and measurement rulers
            const pdfPageSizes = props.pdfPageSizes;
            const imgPageSizes = props.imgPageSizes;
            const scalingImageDataPresent = props.scalingImageDataPresent;
            if (executionType === "direct") {
                if (
                    scalingImageDataPresent &&
                    imgPageSizes.width !== 0 &&
                    imgPageSizes.height !== 0
                ) {
                    if (props.unit !== METRIC) { //IMPERIAL
                        props.scaleUnitValueChange(ScaleService._convertUnitValues(planScaleFeet));
                        ScaleService._setPixelsLength(
                            ScaleService._fixScalingPixels(
                                ScaleService._getPixelsLengthInches(imgPageSizes, pdfPageSizes),
                            ),
                        );
                    } else { //METRIC
                        props.scaleUnitValueChange(planScaleMts / 12);
                        ScaleService._setPixelsLength(
                            ScaleService._getPixelsLengthInches(imgPageSizes, pdfPageSizes),
                        );
                    }

                } else {
                    // This is used to control the scales when no image has loaded
                    if (props.unit !== METRIC) {
                        props.emitLineLenght(150 * planScaleInches);
                        props.scaleUnitValueChange(
                            ScaleService._convertUnitValues(planScaleFeet),
                        );
                    } else {
                        props.emitLineLenght(150);
                        let scaling = planScaleMts; // 300
                        const valueFt = ( 30 * scaling ) / 300;
                        props.scaleUnitValueChange(valueFt);
                    }
                }
            } else {
                // if (this.state.allowSetScaleDirect) {
                props.scaleUnitValueChange(this.state.setScaleDirectValue);
                // }
            }
        } catch (error) {
            console.error("Error on ScaleService.setScaleProcess() :: ", error.message);
        }
    },

    /**
     * Gets Pixels Length in Inches
     * @param imgPageSizes
     * @param pdfPageSizes
     * @param planScaleInches
     * @param props
     * @returns {number}
     * @private
     */
    _getPixelsLengthInches: ( imgPageSizes, pdfPageSizes, planScaleInches, props ) => {
        const POINTS_TO_PIXELS = 1.3333;
        return props.imperialUnit
            ? (
                (
                    ( imgPageSizes.width / pdfPageSizes.inWidth ) * POINTS_TO_PIXELS
                ) * planScaleInches
            )
            : (
                (
                    ( imgPageSizes.width / pdfPageSizes.inWidth )
                ) * planScaleInches
            );
    },

    /**
     * Fix for Scaling on Pixels
     * Correct to proper scaling before they click the OK button
     * @param pixels
     * @param props
     * @returns {number|*}
     * @private
     */
    _fixScalingPixels: ( pixels, props ) => {
        if (props.pdfPageSizes.inWidth && props.pdfPageSizes.inWidth) {
            const width = props.pdfPageSizes.inWidth;
            switch (true) {
                case ( width > 9 && width <= 15.99 ): // 11in
                    return pixels - 0.5;
                case ( width > 16 && width <= 25.99 ): // 24in
                    return pixels - 23.8125;
                case ( width >= 26 && width <= 35.99 ): // 34in
                    return pixels - 23;
                case ( width >= 36 && width <= 42.99 ): // 36in
                    return pixels - 23.12;
                case ( width >= 43 ): // over 43
                    return pixels - 23;
                default:
                    return pixels;
            }
        }
        return pixels;
    },

    _getScale: ( value, props ) => {
        return props.scaleValue;
    },

    /**
     * Emit the proper value of pixels
     * @param setPixels
     * @param props
     * @private
     */
    _setPixelsLength: ( setPixels, props ) => {
        setTimeout(() => {
            if (props.scalePixels === 300) {
                props.emitLineLenght(Number(setPixels));
            } else {
                props.emitLineLenght(Number(props.scalePixels));
            }
        });
    },

    _convertUnitValues: ( val, props ) => {
        return ( props.unit === METRIC ) ? val / 3.28084 : val;
    },

    _timeout( ms ) {
        return new Promise(resolve => setTimeout(resolve, ms));
    },

    async _waitFor( timeoutMs = 0 ) {
        await ScaleService._timeout(timeoutMs);
        return true;
    },
};

export default ScaleService;



// WEBPACK FOOTER //
// ./src/lib/services/scale-service.js